import { useNavigate } from "react-router-dom";
import { Check, MapPinCheck } from "lucide-react";
import { useState } from "react";

import DetailRow from "../../../../components/UI/viewDialogs/DetailRow";
import HeadingDialog from "../../../../components/UI/viewDialogs/HeadingDialog";
import { timeStampToDateAndTime } from "../../../../functions/dateTime/timeStampToDateAndTime";
import { getVehicleReadingStatus } from "../../helpers/getComplaintStatus";
import { checkValue } from "../../../../functions/checkValue";
import Modal from "../../../../components/portal/Modal";
import GradientButton from "../../../../components/UI/buttonUI/GradientButton";
import GrayButton from "../../../../components/UI/buttonUI/GrayButton";
import useImageUrl from "../../../../CustomHooks/useImageUrl";
import { formatTime } from "../../../../functions/dateTime/getTimeString";
import ImageDialog from "../../../../components/UI/viewDialogs/ImageDialog";
import { formatDate } from "../../../../functions/dateTime/formatDate";

export default function VehicleReadingDialog({
  closeDialog,
  details,
  success,
  setConfirmType,
  checkReading,
}) {
  const navigate = useNavigate();
  const { getImageUrl } = useImageUrl();
  const [clickedImage, setClickedImage] = useState(null);

  return (
    <Modal
      onClose={success ? undefined : closeDialog}
      closeIcon={!success}
      modalWidth="view-dialog-content"
    >
      {!success && <HeadingDialog heading="Vehicle Reading Details" />}
      {success && (
        <>
          <Check color="green" className="mx-auto" />
          <p className="view-success-msg-d">
            Successfully Vehicle Reading Added!
          </p>
        </>
      )}
      <div
        className={`${success ? "max-h-[70vh]" : "max-h-[80vh]"} px-2 overflow-auto`}
      >
        <div className="view-dialog-content-wrap">
          <DetailRow title="Vehicle Number" value={details.vehicleNumber} />
          <DetailRow title="Vehicle Date" value={formatDate(details.logDate)} />
          <DetailRow
            title="Morning Reading Time"
            value={formatTime(details.morningReadingAt)}
          />
          <DetailRow title="Morning Reading" value={details.morningReading} />
          <DetailRow
            title="Evening Reading Time"
            value={formatTime(details.eveningReadingAt)}
          />
          <DetailRow title="Evening Reading" value={details.eveningReading} />
          <DetailRow title="Distance KM" value={`${details.distanceKm} km`} />

          <div
            className={`${getVehicleReadingStatus(details.status).textColor}`}
          >
            <DetailRow
              title="Status"
              value={getVehicleReadingStatus(details.status).status}
            />
          </div>
          <DetailRow title="Created By" value={details.createdBy.fullName} />
          <DetailRow
            title="Created At"
            value={timeStampToDateAndTime(details.createdAt)}
          />
          <DetailRow
            title="Checked By"
            value={checkValue(details.checkedBy?.fullName)}
          />
          <DetailRow
            title="Checked At"
            value={timeStampToDateAndTime(details.checkedAt)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {[
            ["Morning Reading Photo", details.morningPhoto],
            ["Evening Reading Photo", details.eveningPhoto],
          ].map(([title, photo]) => (
            <div className="border rounded-lg p-3" key={title}>
              <h4 className="mb-2 font-interBold text-sm text-gray-700">
                {title}
              </h4>
              <div className="h-32 flex items-center justify-center rounded-md bg-gray-100">
                <img
                  src={getImageUrl(photo)}
                  alt={title}
                  onClick={() => setClickedImage(photo)}
                  className="h-full w-full object-contain p-1 cursor-pointer"
                />
              </div>
            </div>
          ))}
        </div>
        {details.stations?.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-2 font-interBold text-sm text-gray-700">
              Stations
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {details.stations.map((station) => (
                <div
                  key={station.id}
                  className="border rounded-lg p-3 bg-gray-50"
                >
                  <div className="flex gap-1 text-dark-blue">
                    <MapPinCheck size={20} />
                    <p className="text-sm">
                      {station.name} ({station.id})
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {checkReading && (
        <div className="flex-center-align gap-2 mt-3">
          <GradientButton
            btnText="Accept"
            onClick={() => setConfirmType(true)}
          />
          <GrayButton
            btnText="Reject"
            width="w-full"
            onClick={() => setConfirmType(false)}
          />
        </div>
      )}
      {success && (
        <div className="pt-5 text-center">
          <GradientButton
            btnText="Ok"
            width="w-28"
            onClick={() => navigate("/vehicle-readings")}
          />
        </div>
      )}
      {clickedImage !== null && (
        <ImageDialog
          image={getImageUrl(clickedImage)}
          closeDialog={() => setClickedImage(null)}
        />
      )}
    </Modal>
  );
}
